import { useState, useEffect, useMemo } from "react";
import { useParams, Link } from "react-router-dom";
import StickyHeader from "@/components/StickyHeader";
import Breadcrumbs from "@/components/Breadcrumbs";
import SiteFooter from "@/components/SiteFooter";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { ArrowLeft, Clock, CalendarDays, Phone, User } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { prepareArticle } from "@/lib/articleHtml";

interface BlogPostData {
  title: string;
  slug: string;
  excerpt: string | null;
  meta_description: string | null;
  content: string;
  created_at: string;
  author_name: string | null;
  read_minutes: number | null;
  category_name: string | null;
}

export default function BlogPostPage() {
  const { slug } = useParams();
  const [post, setPost] = useState<BlogPostData | null>(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    const fetchPost = async () => {
      if (!slug) { setNotFound(true); setLoading(false); return; }
      try {
        const { data, error } = await supabase
          .from('blog_posts')
          .select('title, slug, excerpt, meta_description, content, created_at, author_name, read_minutes, blog_categories(name)')
          .eq('slug', slug)
          .maybeSingle();
        if (error) throw error;
        if (data) {
          setPost({
            title: data.title,
            slug: data.slug,
            excerpt: data.excerpt,
            meta_description: (data as any).meta_description ?? null,
            content: data.content,
            created_at: data.created_at,
            author_name: (data as any).author_name ?? null,
            read_minutes: (data as any).read_minutes ?? null,
            category_name: (data as any).blog_categories?.name || null,
          });
        } else { setNotFound(true); }
      } catch { setNotFound(true); }
      finally { setLoading(false); }
    };
    fetchPost();
  }, [slug]);

  useEffect(() => {
    if (!slug) return;
    supabase.functions.invoke('track-click', {
      body: { slug, referrer: document.referrer || null, path: window.location.pathname + window.location.search },
    }).catch(() => { /* tracking is best-effort */ });
  }, [slug]);

  const article = useMemo(
    () => (post ? prepareArticle(post.content) : { html: "", toc: [], h1: null }),
    [post],
  );

  useEffect(() => {
    if (!post?.title) return;
    document.title = post.title;
    const description =
      post.meta_description ||
      post.excerpt ||
      `Read ${post.title} - expert heating and air conditioning advice from ACG Southern Highlands.`;

    const setMeta = (attr: string, key: string, value: string) => {
      let el = document.querySelector(`meta[${attr}="${key}"]`);
      if (el) { el.setAttribute("content", value); }
      else {
        el = document.createElement("meta");
        el.setAttribute(attr, key);
        (el as HTMLMetaElement).content = value;
        document.head.appendChild(el);
      }
    };

    setMeta("name", "description", description);
    setMeta("property", "og:title", post.title);
    setMeta("property", "og:description", description);
    setMeta("property", "og:type", "article");
    setMeta("property", "og:url", window.location.href);
    setMeta("name", "twitter:title", post.title);
    setMeta("name", "twitter:description", description);
    setMeta("name", "twitter:card", "summary_large_image");

    let canonical = document.querySelector('link[rel="canonical"]');
    if (canonical) { canonical.setAttribute("href", window.location.href); }
    else {
      canonical = document.createElement("link");
      canonical.setAttribute("rel", "canonical");
      canonical.setAttribute("href", window.location.href);
      document.head.appendChild(canonical);
    }

    return () => { document.title = "ACG Southern Highlands"; };
  }, [post?.title, post?.excerpt, post?.meta_description]);

  if (loading) {
    return (<><StickyHeader /><main className="py-20 text-center"><div className="h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent mx-auto" /></main><SiteFooter /></>);
  }

  if (notFound || !post) {
    return (
      <><StickyHeader />
        <div className="container mx-auto px-4 py-24 text-center">
          <h1 className="text-2xl font-bold mb-4">Article not found</h1>
          <Button asChild><Link to="/blog">Back to Blog</Link></Button>
        </div>
        <SiteFooter />
      </>
    );
  }

  const headline = article.h1 || post.title;

  return (
    <>
      <StickyHeader />
      <Breadcrumbs items={[{ label: "Blog", href: "/blog" }, { label: post.title }]} />
      <main>
        <article className="py-8 md:py-14">
          <div className="container mx-auto px-4 max-w-6xl w-full">
            <Button asChild variant="ghost" size="sm" className="mb-6">
              <Link to="/blog"><ArrowLeft className="h-4 w-4 mr-1" /> Back to Blog</Link>
            </Button>

            <header className="mb-8 border-b border-border pb-8">
              {post.category_name && <Badge variant="outline" className="mb-4">{post.category_name}</Badge>}
              <h1 className="text-3xl md:text-5xl font-extrabold leading-tight mb-4">{headline}</h1>
              {(post.meta_description || post.excerpt) && (
                <p className="text-lg text-muted-foreground max-w-3xl">
                  {post.meta_description || post.excerpt}
                </p>
              )}
              <div className="flex flex-wrap items-center gap-x-6 gap-y-2 mt-6 text-sm text-muted-foreground">
                {post.author_name && (
                  <span className="inline-flex items-center gap-1.5"><User className="h-4 w-4" />{post.author_name}</span>
                )}
                <span className="inline-flex items-center gap-1.5">
                  <CalendarDays className="h-4 w-4" />
                  {new Date(post.created_at).toLocaleDateString("en-AU", { day: "numeric", month: "long", year: "numeric" })}
                </span>
                {post.read_minutes ? (
                  <span className="inline-flex items-center gap-1.5"><Clock className="h-4 w-4" />{post.read_minutes} min read</span>
                ) : null}
              </div>
            </header>

            <div className="grid gap-10 lg:grid-cols-[minmax(0,1fr)_300px]">
              <div
                className="article-body max-w-3xl"
                dangerouslySetInnerHTML={{ __html: article.html }}
              />

              <aside className="space-y-6 lg:sticky lg:top-28 lg:self-start">
                {article.toc.length > 1 && (
                  <nav aria-label="On this page" className="rounded-2xl border border-border bg-card p-5">
                    <h2 className="text-sm font-bold uppercase tracking-wide text-muted-foreground mb-3">On this page</h2>
                    <ul className="space-y-2 text-sm">
                      {article.toc.map((item) => (
                        <li key={item.id}>
                          <a href={`#${item.id}`} className="text-foreground/80 hover:text-primary transition-colors">
                            {item.text}
                          </a>
                        </li>
                      ))}
                    </ul>
                  </nav>
                )}

                <div className="rounded-2xl bg-primary text-primary-foreground p-6">
                  <h2 className="text-lg font-bold mb-2">Talk to a local specialist</h2>
                  <p className="text-sm opacity-90 mb-4">
                    Heating and cooling designed for Southern Highlands frosts — Bowral to Goulburn.
                  </p>
                  <Button asChild variant="secondary" size="sm" className="w-full mb-2">
                    <a href="tel:0480841485"><Phone className="h-4 w-4 mr-1" /> 0480 841 485</a>
                  </Button>
                  <Button asChild variant="outline" size="sm" className="w-full bg-transparent border-primary-foreground/40 text-primary-foreground hover:bg-primary-foreground hover:text-primary">
                    <Link to="/schedule-quote">Get a free quote</Link>
                  </Button>
                </div>
              </aside>
            </div>

            <div className="mt-14 p-8 rounded-2xl bg-muted text-center">
              <h2 className="text-2xl font-bold mb-2">Need heating or air conditioning help?</h2>
              <p className="text-muted-foreground mb-5">
                ACG Southern Highlands installs, repairs and services reverse cycle systems built for sub-zero mornings.
              </p>
              <div className="flex flex-wrap justify-center gap-3">
                <Button asChild size="lg"><Link to="/services">Our Services</Link></Button>
                <Button asChild size="lg" variant="outline"><a href="tel:0480841485">Call 0480 841 485</a></Button>
              </div>
            </div>
          </div>
        </article>
      </main>
      <SiteFooter />
    </>
  );
}
